import { createSelector } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useChatContext } from 'stream-chat-react';

import { useFeatureFlags } from 'src/context/flagsContext';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { initializeClientAction } from 'src/store/messages/actions';

const selector = createSelector(
  [
    (state: RootState) => state.users.users,
    (state: RootState) => state.users.internalUsers,
    (state: RootState) => state.messages.clientInitialized,
    (state: RootState) => state.user.loaded,
  ],
  (clientUsers, internalUsers, clientInitialized, userLoaded) => ({
    clientUsers,
    internalUsers,
    clientInitialized,
    userLoaded,
  }),
);

export const useChatClient = () => {
  const dispatch = useAppDispatch();
  const { client: chatClient } = useChatContext();
  const { EnableChatWebsocketFallback } = useFeatureFlags();

  const { userLoaded, clientUsers, internalUsers, clientInitialized } =
    useAppSelector(selector);

  /**
   * This effect is responsible for initializing the client.
   * It depends the clientUsers and internalUsers states
   * from where the current user data is retrieved.
   */
  useEffect(() => {
    if (!userLoaded) return;
    // Read by the SDK when it connects: a websocket failure then falls back to long polling.
    chatClient.options.enableWSFallback = EnableChatWebsocketFallback;
    dispatch(initializeClientAction(chatClient));
  }, [
    chatClient,
    userLoaded,
    clientUsers,
    internalUsers,
    EnableChatWebsocketFallback,
  ]);

  return clientInitialized ? chatClient : null;
};
